import { useState, useEffect } from 'react';
import {
    Plus,
    SearchLg,
    Edit01,
    Trash01,
    BookOpen01,
    Mail01,
    XCircle
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { addressBookService } from '@/data/mock-address-book';
import type { AddressBookEntry, AddressBookRole } from '@/types/address-book';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';

const DEFAULT_ACCOUNT_ID = 'ff-1';

export const FreightForwarderAddressBookPage = () => {
    const { t } = useTranslation(['pages:address-book', 'types', 'common']);
    const { translateRole } = useI18n();
    const [entries, setEntries] = useState<AddressBookEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<AddressBookRole | ''>('');
    const [showModal, setShowModal] = useState(false);
    const [editingEntry, setEditingEntry] = useState<AddressBookEntry | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        role: 'Autorità' as AddressBookRole
    });

    const roleOptions = [
        { id: '', label: t('list.allRoles') },
        { id: 'Autorità', label: translateRole('Autorità') },
        { id: 'Freight Forwarder', label: translateRole('Freight Forwarder') },
        { id: 'Sales', label: translateRole('Sales') },
        { id: 'Spedizioniere', label: translateRole('Spedizioniere') },
        { id: 'Dogana', label: translateRole('Dogana') },
        { id: 'Vettore marittimo', label: translateRole('Vettore marittimo') },
        { id: 'Trasporto terrestre', label: translateRole('Trasporto terrestre') },
        { id: 'Porto imbarco', label: translateRole('Porto imbarco') },
        { id: 'Porto di sbarco', label: translateRole('Porto di sbarco') },
        { id: 'Staff aeroporto', label: translateRole('Staff aeroporto') },
        { id: 'Armatore', label: translateRole('Armatore') },
    ];

    const roleFormOptions = roleOptions.filter(option => option.id !== '');

    useEffect(() => {
        const loadEntries = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            const accountId = localStorage.getItem('accountId') || DEFAULT_ACCOUNT_ID;

            const entries = addressBookService.getByAccountId(accountId);
            setEntries(entries);
            setIsLoading(false);
        };
        loadEntries();
    }, []);

    const getRoleColor = (role: string) => {
        const roleColors: Record<string, 'brand' | 'error' | 'warning' | 'success' | 'gray'> = {
            'Autorità': 'error',
            'Freight Forwarder': 'brand',
            'Sales': 'warning',
            'Spedizioniere': 'brand',
            'Dogana': 'error',
            'Vettore marittimo': 'success',
            'Trasporto terrestre': 'success',
            'Porto imbarco': 'brand',
            'Porto di sbarco': 'brand',
            'Staff aeroporto': 'warning',
            'Armatore': 'brand',
        };
        return roleColors[role] || 'gray';
    };

    const handleAddEntry = () => {
        setEditingEntry(null);
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            role: 'Autorità'
        });
        setShowModal(true);
    };

    const handleEditEntry = (entry: AddressBookEntry) => {
        setEditingEntry(entry);
        setFormData({
            firstName: entry.firstName,
            lastName: entry.lastName,
            email: entry.email,
            role: entry.role
        });
        setShowModal(true);
    };

    const handleSaveEntry = () => {
        const accountId = localStorage.getItem('accountId') || DEFAULT_ACCOUNT_ID;

        if (editingEntry) {
            const result = addressBookService.update(editingEntry.id, {
                id: editingEntry.id,
                ...formData,
                accountId
            });
            if (result) {
                window.location.reload();
            }
        } else {
            const result = addressBookService.create({
                ...formData,
                accountId
            });
            if (result) {
                window.location.reload();
            }
        }
    };

    const handleDeleteEntry = (entryId: string) => {
        setConfirmDeleteId(entryId);
    };

    const handleConfirmDelete = () => {
        if (!confirmDeleteId) return;
        const success = addressBookService.delete(confirmDeleteId);
        if (success) {
            window.location.reload();
        } else {
            setConfirmDeleteId(null);
        }
    };

    const filteredEntries = entries.filter(entry => {
        const matchesSearch = !searchTerm ||
            entry.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            entry.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            entry.email.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRole = !roleFilter || entry.role === roleFilter;

        return matchesSearch && matchesRole;
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('list.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('list.subtitle')}
                    </p>
                </div>
                <Button
                    onClick={handleAddEntry}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                    iconLeading={<Plus className="size-5" />}
                >
                    {t('list.addContact')}
                </Button>
            </div>

            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        placeholder={t('list.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.rolePlaceholder')}
                        items={roleOptions}
                        selectedKey={roleFilter}
                        onSelectionChange={(key) => setRoleFilter(key as AddressBookRole | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            <TableCard.Root>
                <TableCard.Header
                    title={t('list.title') + ` (${filteredEntries.length})`}
                    description={t('list.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('list.headers.firstName')} isRowHeader />
                        <Table.Head label={t('list.headers.lastName')} />
                        <Table.Head label={t('list.headers.email')} />
                        <Table.Head label={t('list.headers.role')} />
                        <Table.Head label={t('list.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {filteredEntries.map((entry) => {
                            return (
                                <Table.Row key={entry.id}>
                                    <Table.Cell>
                                        <div className="font-medium text-primary">{entry.firstName}</div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="font-medium text-primary">{entry.lastName}</div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <Mail01 className="size-4 text-tertiary" />
                                            <span className="text-sm text-tertiary">{entry.email}</span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getRoleColor(entry.role)} size="sm">
                                            {translateRole(entry.role)}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleEditEntry(entry)}
                                                className="text-brand-secondary hover:text-brand-secondary_hover"
                                            >
                                                <Edit01 className="size-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteEntry(entry.id)}
                                                className="text-error-600 hover:text-error-700"
                                            >
                                                <Trash01 className="size-4" />
                                            </button>
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {filteredEntries.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <BookOpen01 className="size-12 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-primary mb-2">{t('list.noContacts.title')}</h3>
                        <p className="text-tertiary">
                            {searchTerm || roleFilter
                                ? t('list.noContacts.message')
                                : t('list.noContacts.emptyMessage')
                            }
                        </p>
                    </div>
                    {!searchTerm && !roleFilter && (
                        <Button
                            onClick={handleAddEntry}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                        >
                            <Plus className="size-5" />
                            {t('list.noContacts.addFirst')}
                        </Button>
                    )}
                </div>
            )}

            {showModal && (
                <ModalOverlay isOpen={showModal} onOpenChange={setShowModal}>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-3xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {editingEntry ? t('list.modal.editTitle') : t('list.modal.addTitle')}
                                    </h2>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <div className="space-y-6">
                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.firstName')}
                                        </label>
                                        <Input
                                            placeholder={t('list.modal.fields.firstNamePlaceholder')}
                                            value={formData.firstName}
                                            onChange={(value) => setFormData({ ...formData, firstName: value })}
                                            size="md"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.lastName')}
                                        </label>
                                        <Input
                                            placeholder={t('list.modal.fields.lastNamePlaceholder')}
                                            value={formData.lastName}
                                            onChange={(value) => setFormData({ ...formData, lastName: value })}
                                            size="md"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.email')}
                                        </label>
                                        <Input
                                            placeholder={t('list.modal.fields.emailPlaceholder')}
                                            value={formData.email}
                                            onChange={(value) => setFormData({ ...formData, email: value })}
                                            size="md"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.role')}
                                        </label>
                                        <Select
                                            placeholder={t('list.modal.fields.role', { ns: 'common' })}
                                            items={roleFormOptions}
                                            selectedKey={formData.role}
                                            onSelectionChange={(key) => setFormData({ ...formData, role: key as AddressBookRole })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>
                                    </div>

                                    <div className="flex justify-end gap-3">
                                        <Button
                                            onClick={() => setShowModal(false)}
                                            color="secondary"
                                            size="md"
                                        >
                                            {t('list.modal.cancel')}
                                        </Button>
                                        <Button
                                            onClick={handleSaveEntry}
                                            disabled={!formData.firstName || !formData.lastName || !formData.email}
                                            color="primary"
                                            size="md"
                                        >
                                            {editingEntry ? t('list.modal.save') : t('list.modal.create')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {confirmDeleteId && (
                <ModalOverlay isOpen={!!confirmDeleteId} onOpenChange={(open) => !open && setConfirmDeleteId(null)}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">{t('list.confirmDelete.title')}</h2>
                                    <button
                                        onClick={() => setConfirmDeleteId(null)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <p className="text-tertiary mb-6">{t('list.confirmDelete.message')}</p>
                                <div className="flex justify-end gap-3">
                                    <Button onClick={() => setConfirmDeleteId(null)} color="secondary" size="md">
                                        {t('list.confirmDelete.cancel')}
                                    </Button>
                                    <Button onClick={handleConfirmDelete} color="primary-destructive" size="md">
                                        {t('list.confirmDelete.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};

